/* Lab 1.8 · 评测：一个成功率背后藏了什么.

   The data is the robot-reel stress lab as published (data.json, from make_data.py): 30 trials of a
   SmolVLA policy on one LIBERO task, 10 initial states x 3 conditions, at most 160 actions each.
   Nothing here is simulated. The matrix shows every trial; "pairs" compare the same initial state
   across two conditions; the 3D view replays one trial's end-effector path as recorded (20 Hz),
   and marks its last second, where a failure that ran out of actions is still moving.

   The scripted take (22 s), in three slices for episode 1.8:
     0–7.5   the stress lab: the matrix fills in, row by row, and the paths draw
     7.5–14.5 the three conditions and the action budget: the budget bar, 160 actions = 8 s
     14.5–22 pick a condition and a pair: camera vs reference, "net +2" splits into 3 better, 1 worse;
             the one that got worse, seed 9, replayed: it ran out of actions, still moving */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, run, clamp, span, ease, el } from "../kit/stage";
import { localizePage, tr, type Dict } from "../kit/i18n";
import DATA from "./data.json";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

type Cond = "reference" | "dim" | "camera";
type Filter = "all" | "both" | "better" | "worse" | "neither";
interface Trial { id: string; seed: number; condition: Cond; outcome: string; actions: number; sim_seconds: number; fps: number; eef: number[][]; last_second_path_m: number }
const trials = DATA.trials as Trial[];
const CONDS: Cond[] = ["reference", "dim", "camera"];
const NAME: Record<Cond, [string, string]> = { reference: ["正常光", "reference"], dim: ["暗光", "dim light"], camera: ["挪相机", "camera moved"] };
const SEEDS = [...new Set(trials.map(t => t.seed))].sort((a, b) => a - b);
const T = (seed: number, c: Cond): Trial => trials.find(t => t.seed === seed && t.condition === c)!;
const ok = (t: Trial): boolean => t.outcome === "success";
const BUDGET = DATA.source.action_budget;

function pair(seed: number, c: Cond): Filter {
  const a = ok(T(seed, "reference")), b = ok(T(seed, c));
  return a && b ? "both" : !a && b ? "better" : a && !b ? "worse" : "neither";
}

// --- the scripted take
interface Shot { fill: number; cond: Cond; split: boolean; filter: Filter; seed: number; replay: number; budget: boolean }
function scripted(t: number): Shot {
  if (t < 7.5) return { fill: ease(span(t, .6, 5.6)), cond: "camera", split: false, filter: "all", seed: SEEDS[Math.min(9, Math.floor(span(t, .6, 5.6) * 10))], replay: span(t, 1, 7.5), budget: false };
  if (t < 14.5) return { fill: 1, cond: t < 10 ? "reference" : t < 12 ? "dim" : "camera", split: false, filter: "all", seed: 1, replay: span(t, 7.5, 14.5), budget: true };
  const l = t - 14.5;
  return { fill: 1, cond: "camera", split: l > 2.2, filter: l < 4.2 ? "all" : l < 5.4 ? "better" : "worse", seed: 9, replay: span(l, 2.4, 6.8), budget: true };
}

// --- scene: the recorded end-effector paths, above a floor; one trial highlighted
const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
// the paths sit in the empty lower right of the frame, clear of the matrix and the trial panel
camera.setViewOffset(1920, 1080, -330, -150, 1920, 1080);
floor(scene);
// robot frame (x forward, y left, z up) to the scene (y up), centred on the paths
const CX = -.06, CY = .1, CZ = .9;
const toScene = (p: number[]): THREE.Vector3 => new THREE.Vector3((p[0] - CX) * 2.2, (p[2] - CZ) * 2.2 + .02, -(p[1] - CY) * 2.2);
const lines = new Map<string, THREE.Line>();
for (const tl of trials) {
  const geo = new THREE.BufferGeometry().setFromPoints(tl.eef.map(toScene));
  const mat = new THREE.LineBasicMaterial({ color: ok(tl) ? C.see : C.amber, transparent: true, opacity: .2 });
  const line = new THREE.Line(geo, mat);
  scene.add(line);
  lines.set(tl.id, line);
}
const hiGeo = new THREE.BufferGeometry(), tailGeo = new THREE.BufferGeometry();
const hi = new THREE.Line(hiGeo, new THREE.LineBasicMaterial({ color: C.ink, transparent: true, opacity: .95 }));
const tail = new THREE.Line(tailGeo, new THREE.LineBasicMaterial({ color: C.amber }));
scene.add(hi, tail);
const tipMat = new THREE.MeshStandardMaterial({ color: C.act, emissive: C.act, emissiveIntensity: 1 });
const tip = new THREE.Mesh(new THREE.SphereGeometry(.018, 24, 16), tipMat);
scene.add(tip);
const start = new THREE.Mesh(new THREE.RingGeometry(.02, .028, 32), new THREE.MeshBasicMaterial({ color: C.muted, side: THREE.DoubleSide }));
start.rotation.x = -Math.PI / 2;
scene.add(start);

// --- live state and controls
const controls = new OrbitControls(camera, canvas);
controls.target.set(0, .34, 0);
Object.assign(controls, { enableDamping: true, minDistance: .6, maxDistance: 3, maxPolarAngle: 1.5, enabled: !recorded });
camera.position.set(-1.07, 1.05, 1.19);
const user: Shot = { fill: 1, cond: "camera", split: false, filter: "all", seed: 9, replay: 0, budget: true };
let replayT0 = 0;
const segBind = (id: string, key: "cond" | "filter", attr: string): void => el(id).addEventListener("click", e => {
  const v = (e.target as HTMLElement).dataset[attr];
  if (v) (user as unknown as Record<string, string>)[key] = v;
});
segBind("cond", "cond", "c");
segBind("filter", "filter", "f");
el<HTMLInputElement>("split").addEventListener("change", e => { user.split = (e.target as HTMLInputElement).checked; });
const seedIn = el<HTMLInputElement>("seed");
seedIn.addEventListener("input", () => { user.seed = Number(seedIn.value); replayT0 = performance.now(); });
el("jump").addEventListener("click", () => { replayT0 = performance.now() - 7000; });

// the matrix, built once; every frame only sets classes
const grid = el("grid");
grid.innerHTML = `<div></div>${SEEDS.map(s => `<div class="h">${String(s).padStart(2, "0")}</div>`).join("")}` +
  CONDS.map(c => `<div class="rowname">${tr(NAME[c][0])}<small>${NAME[c][1]}</small></div>` +
    SEEDS.map(s => `<div class="cell" id="c-${c}-${s}" data-seed="${s}" data-c="${c}"></div>`).join("")).join("");
grid.addEventListener("click", e => {
  const cell = (e.target as HTMLElement).closest(".cell") as HTMLElement | null;
  if (!cell || recorded) return;
  user.seed = Number(cell.dataset.seed);
  if (cell.dataset.c !== "reference") user.cond = cell.dataset.c as Cond;
  seedIn.value = String(user.seed);
  replayT0 = performance.now();
});

function frameAt(t: number): void {
  const s: Shot = recorded ? scripted(t) : { ...user, replay: clamp((performance.now() - replayT0) / 8000, 0, 1) };

  // matrix
  const shown = Math.round(s.fill * 30);
  for (const c of CONDS) for (const seed of SEEDS) {
    const tl = T(seed, c), cell = el(`c-${c}-${seed}`), p = pair(seed, s.cond);
    const visible = CONDS.indexOf(c) * 10 + SEEDS.indexOf(seed) < shown;
    cell.className = "cell" + (visible ? (ok(tl) ? " s" : " f") : "");
    const inFilter = s.filter === "all" || p === s.filter;
    const relevant = c === "reference" || c === s.cond;
    if (s.split && (!inFilter || !relevant)) cell.classList.add("dim");
    if (!s.split && s.filter !== "all" && !inFilter) cell.classList.add("dim");
    if (seed === s.seed && (c === s.cond || c === "reference")) cell.classList.add("pick");
    cell.innerHTML = !visible ? "" : c !== "reference" && s.split && (p === "better" || p === "worse") ? `<span class="arr">${p === "better" ? "▲" : "▼"}</span>` : ok(tl) ? "✓" : "·";
  }
  el("mode").textContent = s.split ? tr("配对拆开 · {0} vs 正常光", tr(NAME[s.cond][0])) : tr("净增");
  const better = SEEDS.filter(x => pair(x, s.cond) === "better").length, worse = SEEDS.filter(x => pair(x, s.cond) === "worse").length;
  const net = SEEDS.filter(x => ok(T(x, s.cond))).length - SEEDS.filter(x => ok(T(x, "reference"))).length;
  el("vNet").textContent = s.cond === "reference" ? "—" : (net >= 0 ? "+" : "−") + Math.abs(net);
  el("vSplit").innerHTML = s.cond === "reference" ? "—" : `<span class="good">${better}</span> / <span class="bad">${worse}</span>`;
  const fails = trials.filter(x => !ok(x));
  el("vFail").textContent = `${fails.filter(x => x.actions >= BUDGET).length}/${fails.length}`;
  el("tNet").classList.toggle("on", !s.split && s.cond !== "reference");
  el("tSplit").classList.toggle("on", s.split);
  el("tFail").classList.toggle("on", s.budget && !s.split);
  document.querySelectorAll<HTMLButtonElement>("#cond button").forEach(b => b.classList.toggle("on", b.dataset.c === s.cond));
  document.querySelectorAll<HTMLButtonElement>("#filter button").forEach(b => b.classList.toggle("on", b.dataset.f === s.filter));

  // the chosen trial: its path draws with the replay; the last second in amber
  const tl = T(s.seed, s.cond);
  for (const x of trials) {   // a path appears when its cell does
    const line = lines.get(x.id)!, m = line.material as THREE.LineBasicMaterial;
    m.opacity = x.id === tl.id ? 0 : x.condition === s.cond ? .28 : .06;
    line.visible = CONDS.indexOf(x.condition) * 10 + SEEDS.indexOf(x.seed) < shown;
  }
  const pts = tl.eef.map(toScene), k = Math.max(2, Math.round(s.replay * pts.length));
  hiGeo.setFromPoints(pts.slice(0, k));
  const tailStart = pts.length - tl.fps;
  tailGeo.setFromPoints(k > tailStart ? pts.slice(tailStart, k) : []);
  tip.position.copy(pts[k - 1]);
  const done = k >= pts.length;
  tipMat.color.set(done ? (ok(tl) ? C.see : C.amber) : C.act);
  tipMat.emissive.copy(tipMat.color);
  start.position.copy(pts[0]).setY(.003);

  // trial panel
  el("meta").innerHTML = tr("起始状态 {0} · 条件 {1}", `<b>${String(tl.seed).padStart(2, "0")}</b>`, `<b>${tr(NAME[tl.condition][0])}</b>`) + "<br>" +
    tr("动作 {0} / {1} · 仿真 {2} s", `<b>${Math.round(tl.actions * s.replay)}</b>`, BUDGET, `<b>${(tl.sim_seconds * s.replay).toFixed(1)}</b>`) + "<br>" +
    tr("最后一秒末端还走了 {0}", `<b>${(tl.last_second_path_m * 100).toFixed(1)} cm</b>`);
  const b = el("budget");
  (b.querySelector("i") as HTMLElement).style.width = (tl.actions / BUDGET * s.replay * 100).toFixed(1) + "%";
  b.classList.toggle("full", tl.actions >= BUDGET && s.replay > .98);
  const v = el("verdict");
  if (!done) { v.className = "verdict busy"; v.innerHTML = `▸ REPLAY<small>${tr("按记录回放末端轨迹（20 Hz）")}</small>`; }
  else if (ok(tl)) { v.className = "verdict ok"; v.innerHTML = `✓ ${tr("SUCCESS · 成功")}<small>${tr("用了 {0} 个动作", tl.actions)}</small>`; }
  else { v.className = "verdict warn"; v.innerHTML = `! ${tr("STEP LIMIT · 动作用完")}<small>${tr("到点被停下，手还在动：不是“做错了停住”，是“没做完”")}</small>`; }

  if (recorded) {
    const az = 2.3 + t * .01, o = controls.target.set(0, .34, 0);
    camera.position.set(o.x + Math.cos(az) * 1.6, 1.05, o.z + Math.sin(az) * 1.6);
    camera.lookAt(o);
  } else controls.update();
  render();
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
}

run(frameAt, { dur: Number(q.get("dur") || 22), still: 19 });
